import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/app';
import type { BaseFilter } from '@/mixins/filterHandler/core/base-filter';

// Type for filter collection (object with filter instances)
type FilterCollection = Record<string, BaseFilter<any>>;

interface Props {
	storageKey?: string;
	loading?: boolean;
	showSearchButton?: boolean;
	showFlushFiltersButton?: boolean;
	dataFilters?: FilterCollection | null;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SearchLayout',
  props: {
    storageKey: null,
    loading: { type: Boolean },
    showSearchButton: { type: Boolean, default: true },
    showFlushFiltersButton: { type: Boolean, default: true },
    dataFilters: { default: null }
  },
  emits: ['search', 'flush-filter'],
  setup(__props: any, { emit }) {

const props = __props as { storageKey?: string, loading?: boolean, showSearchButton: boolean, showFlushFiltersButton: boolean, dataFilters: FilterCollection | null };





const appStore = useAppStore();
const { filtersCollapsedState, isSidebarOpened } = storeToRefs(appStore);

// Sticky header management
const stickyHeaderRef = ref<HTMLElement | null>(null);
const isSticky = ref(false);
const isFixed = ref(false);
const headerOffset = ref(0);
const headerWidth = ref(0);

const fixedStyles = computed((): Record<string, string | number> => {
	if (!isFixed.value) {
		return {};
	}
	return {
		position: 'fixed' as const,
		top: '50px',
		left: `${headerOffset.value}px`,
		width: `${headerWidth.value}px`,
		zIndex: 100,
	};
});

const updateDimensions = () => {
	if (!stickyHeaderRef.value) {
		return;
	}

	// Temporaneamente rimuovi il fixed per ottenere le dimensioni corrette
	const wasFixed = isFixed.value;
	if (wasFixed) {
		isFixed.value = false;
	}

	// Forza un reflow e ottieni le dimensioni aggiornate
	requestAnimationFrame(() => {
		if (!stickyHeaderRef.value) {
			return;
		}

		const rect = stickyHeaderRef.value.getBoundingClientRect();
		headerOffset.value = rect.left;
		headerWidth.value = rect.width;

		// Ripristina lo stato fixed se era attivo
		if (wasFixed) {
			isFixed.value = true;
		}
	});
};

const handleScroll = () => {
	if (!stickyHeaderRef.value) {
		return;
	}

	const rect = stickyHeaderRef.value.getBoundingClientRect();
	const navbarHeight = 50; // $navbar-height

	// Se l'header sta per uscire dalla viewport in alto, lo fissiamo
	if (rect.top <= navbarHeight && !isFixed.value) {
		headerOffset.value = rect.left;
		headerWidth.value = rect.width;
		isFixed.value = true;
		isSticky.value = true;
	} else if (window.scrollY === 0 || rect.top > navbarHeight) {
		isFixed.value = false;
		isSticky.value = false;
	}

	// Aggiorna le dimensioni se è già fixed
	if (isFixed.value) {
		updateDimensions();
	}
};

const handleResize = () => {
	if (isFixed.value) {
		updateDimensions();
	}
};

// Watch per cambiamenti della sidebar
watch(isSidebarOpened, () => {
	// Aspetta che l'animazione della sidebar finisca (280ms come da CSS)
	setTimeout(() => {
		if (isFixed.value) {
			updateDimensions();
		}
	}, 300);
});

onMounted(() => {
	window.addEventListener('scroll', handleScroll, { passive: true });
	window.addEventListener('resize', handleResize, { passive: true });
});

onUnmounted(() => {
	window.removeEventListener('scroll', handleScroll);
	window.removeEventListener('resize', handleResize);
});

// Computed reattivo che legge/scrive direttamente sullo state
const isCollapsed = computed({
	get: () => {
		if (!props.storageKey) {
			return false;
		}
		return filtersCollapsedState.value[props.storageKey] ?? false;
	},
	set: (value: boolean) => {
		if (props.storageKey) {
			appStore.setFiltersCollapsed(props.storageKey, value);
		}
	},
});

// Computed per il summary dei filtri attivi
const activeFiltersSummary = computed((): string | null => {
	// Se dataFilters non è passato, non mostrare nulla
	if (!props.dataFilters) {
		return null;
	}

	// Filtra solo i filtri con valore attivo
	const activeFilters = Object.values(props.dataFilters).filter((filter) => {
		const value = filter.value;

		// Considera attivo se il valore è diverso da null/undefined/stringa vuota
		if (value === null || value === undefined || value === '') {
			return false;
		}

		// Per array, verifica che non sia vuoto
		if (Array.isArray(value)) {
			return value.length > 0;
		}

		return true;
	});

	// Se non ci sono filtri attivi, non mostrare nulla
	if (activeFilters.length === 0) {
		return null;
	}

	// Costruisci la stringa con le descrizioni
	const labels = activeFilters.map((filter) => filter.filterDescription);
	const count = activeFilters.length;
	const filtersWord = count === 1 ? 'filtro' : 'filtri';

	return `${labels.join(', ')} (${count} ${filtersWord})`;
});

const toggleCollapse = () => {
	isCollapsed.value = !isCollapsed.value;
};

const handleSearch = () => {
	emit('search');
};

const handleFlushFilter = () => {
	emit('flush-filter');
};

return { __sfc: true,props, emit, appStore, filtersCollapsedState, isSidebarOpened, stickyHeaderRef, isSticky, isFixed, headerOffset, headerWidth, fixedStyles, updateDimensions, handleScroll, handleResize, isCollapsed, activeFiltersSummary, toggleCollapse, handleSearch, handleFlushFilter }
}

})